// The getting-started checklist on Home (T-427, US-185, UX-009, UX-101, UXD-24) and the
// composer's "No CRM connected yet" line. Steps follow the person's rights and are ticked off by
// what happened; "Connect your CRM" comes first and stays for every role until a CRM is
// connected: people who manage integrations connect it, everyone else asks them.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { api, problemText } from "../api";
import type { components } from "../api-schema";
import { formatDate, t, type MessageKey } from "../i18n";
import { useToast } from "./overlay";
import { Button, Card } from "./ui";

type Checklist = components["schemas"]["Checklist"];
type Step = components["schemas"]["Step"];
type Patch = components["schemas"]["ChecklistPatch"];

const HUB = "/settings?section=integrations";

/// Where each step's action leads.
const ACTION: Record<string, string> = {
  crm: HUB,
  studio: "/help/recording-with-studio",
  record: "/record",
  send: "/demos/new",
  invite: "/settings?section=people",
};

export function useChecklist() {
  return useQuery({ queryKey: ["checklist"], queryFn: () => api.get<Checklist>("/v1/me/checklist") });
}

function useChecklistActions() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const patch = useMutation({
    mutationFn: (body: Patch) => api.patch<Checklist>("/v1/me/checklist", body),
    onSuccess: (data) => queryClient.setQueryData(["checklist"], data),
    onError: (e) => toast(problemText(e), "error"),
  });
  const ask = useMutation({
    mutationFn: () => api.post<{ told: number }>("/v1/company/crm/request"),
    onSuccess: (r) => {
      toast(r.told > 0 ? t("checklist.asked", { count: r.told }) : t("checklist.askedAlready"), "success");
      void queryClient.invalidateQueries({ queryKey: ["checklist"] });
    },
    onError: (e) => toast(problemText(e), "error"),
  });
  return { patch, ask };
}

/// The CRM row's action: Connect for people who manage integrations, Ask your admin otherwise.
function CrmAction({ list, compact }: { list: Checklist; compact?: boolean }) {
  const { ask } = useChecklistActions();
  const crm = list.steps.find((s) => s.key === "crm");
  if (!crm || crm.done) return null;
  if (crm.can_act)
    return (
      <Link to={HUB} className={`text-xs text-blue no-underline hover:underline ${compact ? "" : "ml-auto"}`}>
        {t("checklist.connect")}
      </Link>
    );
  return (
    <span className={`flex items-center gap-2 ${compact ? "" : "ml-auto"}`}>
      {list.crm_asked_at && <span className="text-xs text-steel">{t("checklist.askedOn", { date: formatDate(list.crm_asked_at) })}</span>}
      <button type="button" className="border-0 bg-transparent p-0 text-xs text-blue hover:underline" disabled={ask.isPending} onClick={() => ask.mutate()}>
        {list.crm_asked_at ? t("checklist.askAgain") : t("checklist.ask")}
      </button>
    </span>
  );
}

function Row({ step, list }: { step: Step; list: Checklist }) {
  const { patch } = useChecklistActions();
  const label = t(`checklist.step.${step.key}` as MessageKey);
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-rule-soft py-2 last:border-b-0">
      <span aria-hidden className={`h-4 w-4 shrink-0 rounded-[4px] border-[1.5px] ${step.done ? "border-blue bg-blue" : "border-rule"}`} />
      <span className={step.done ? "text-steel line-through" : step.skipped ? "text-steel" : ""}>{label}</span>
      {step.done && <span className="sr-only">{t("home.check.done")}</span>}
      {!step.done && step.skipped && (
        <span className="ml-auto flex items-center gap-2 text-xs text-steel">
          {t("checklist.skipped")}
          <button type="button" className="border-0 bg-transparent p-0 text-xs text-blue hover:underline" onClick={() => patch.mutate({ unskip: step.key })}>
            {t("checklist.unskip")}
          </button>
        </span>
      )}
      {!step.done && !step.skipped && step.key === "crm" && (
        <span className="ml-auto flex items-center gap-3">
          {step.can_skip && (
            <button type="button" className="border-0 bg-transparent p-0 text-xs text-steel hover:underline" onClick={() => patch.mutate({ skip: step.key })}>
              {t("checklist.skip")}
            </button>
          )}
          <CrmAction list={list} compact />
        </span>
      )}
      {!step.done && !step.skipped && step.key !== "crm" && (
        <span className="ml-auto flex items-center gap-3">
          <button type="button" className="border-0 bg-transparent p-0 text-xs text-steel hover:underline" onClick={() => patch.mutate({ skip: step.key })}>
            {t("checklist.skip")}
          </button>
          {/* Studio as a download where this server offers one (STUDIO_DOWNLOAD_URL). */}
          {step.key === "studio" && list.studio_download_url && (
            <a href={list.studio_download_url} download className="text-xs text-blue no-underline hover:underline">
              {t("checklist.download.studio")}
            </a>
          )}
          <Link to={ACTION[step.key] ?? "/"} className="text-xs text-blue no-underline hover:underline">
            {t(`checklist.go.${step.key}` as MessageKey)}
          </Link>
        </span>
      )}
    </div>
  );
}

export function HomeChecklist() {
  const checklist = useChecklist();
  const { patch } = useChecklistActions();
  const list = checklist.data;
  if (!list) return null;
  const finished = list.steps.filter((s) => s.done || s.skipped).length;
  const complete = finished === list.steps.length;
  if (list.hidden || complete) {
    // Only the CRM row stays, until a CRM is connected (UXD-24).
    if (list.crm_connected) return null;
    return (
      <Card>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span aria-hidden className="h-4 w-4 shrink-0 rounded-[4px] border-[1.5px] border-rule" />
          <span>{t("checklist.step.crm")}</span>
          <CrmAction list={list} />
        </div>
      </Card>
    );
  }
  const done = list.steps.filter((s) => s.done).length;
  return (
    <Card>
      <div className="mb-2 flex items-center gap-3">
        <h3 className="m-0 text-xs font-medium uppercase tracking-[0.06em] text-steel">{t("home.checklist")}</h3>
        <span className="text-xs text-steel">{t("checklist.progress", { done, total: list.steps.length })}</span>
        <button type="button" className="ml-auto border-0 bg-transparent p-0 text-xs text-steel hover:underline" onClick={() => patch.mutate({ hidden: true })}>
          {t("checklist.hide")}
        </button>
      </div>
      <div className="mb-1 h-1 overflow-hidden rounded-full bg-panel" role="progressbar" aria-valuemin={0} aria-valuemax={list.steps.length} aria-valuenow={done} aria-label={t("home.checklist")}>
        <div className="h-full bg-blue" style={{ width: `${(100 * done) / Math.max(1, list.steps.length)}%` }} />
      </div>
      {list.steps.map((step) => (
        <Row key={step.key} step={step} list={list} />
      ))}
    </Card>
  );
}

/// The composer's muted line while no CRM is connected (UX-103, US-072): never blocks sending,
/// can be closed, and disappears once a CRM is connected.
export function NoCrmHint() {
  const checklist = useChecklist();
  const { patch } = useChecklistActions();
  const list = checklist.data;
  if (!list || list.crm_connected || list.crm_hint_dismissed) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[7px] bg-panel px-3 py-2 text-xs text-steel">
      <span>{t("composer.noCrm")}</span>
      <CrmAction list={list} compact />
      <Button size="sm" kind="ghost" className="ml-auto" aria-label={t("checklist.dismissHint")} onClick={() => patch.mutate({ crm_hint_dismissed: true })}>
        ×
      </Button>
    </div>
  );
}
